import type { TransactionItem } from '../../interface/transactions';
import type { TransactionMove } from '../TransactionContainer';

const idsOf = (items: readonly TransactionItem[], invoiceId: string): string[] =>
  items.filter((item) => item.invoiceId === invoiceId).map((item) => item.id);

const withInvoiceOrder = (
  items: readonly TransactionItem[],
  invoiceId: string,
  orderedIds: readonly string[]
): readonly TransactionItem[] => {
  const byId = new Map(items.map((item) => [item.id, item]));
  let next = 0;
  return items.map((item) =>
    item.invoiceId === invoiceId ? (byId.get(orderedIds[next++]) as TransactionItem) : item
  );
};

export function movesFor(items: readonly TransactionItem[], id: string): readonly TransactionMove[] {
  const item = items.find((candidate) => candidate.id === id);
  if (!item) return [];
  const ids = idsOf(items, item.invoiceId);
  if (ids.length < 2) return [];
  const at = ids.indexOf(id);
  const moves: TransactionMove[] = [];
  if (at > 0) moves.push('top', 'up');
  if (at < ids.length - 1) moves.push('down', 'bottom');
  return moves;
}

export function applyMove(
  items: readonly TransactionItem[],
  id: string,
  move: TransactionMove
): readonly TransactionItem[] | undefined {
  const item = items.find((candidate) => candidate.id === id);
  if (!item) return undefined;
  const ids = idsOf(items, item.invoiceId);
  const at = ids.indexOf(id);
  const to = move === 'top' ? 0 : move === 'up' ? at - 1 : move === 'down' ? at + 1 : ids.length - 1;
  if (to < 0 || to >= ids.length || to === at) return undefined;
  const ordered = ids.filter((candidate) => candidate !== id);
  ordered.splice(to, 0, id);
  return withInvoiceOrder(items, item.invoiceId, ordered);
}

export function dropWithinInvoice(
  items: readonly TransactionItem[],
  draggedId: string,
  targetId: string,
  side: 'before' | 'after'
): readonly TransactionItem[] | undefined {
  const dragged = items.find((candidate) => candidate.id === draggedId);
  const target = items.find((candidate) => candidate.id === targetId);
  if (!dragged || !target || draggedId === targetId || dragged.invoiceId !== target.invoiceId) {
    return undefined;
  }
  const ids = idsOf(items, dragged.invoiceId);
  const ordered = ids.filter((candidate) => candidate !== draggedId);
  const at = ordered.indexOf(targetId);
  ordered.splice(side === 'before' ? at : at + 1, 0, draggedId);
  return ordered.every((id, position) => id === ids[position])
    ? undefined
    : withInvoiceOrder(items, dragged.invoiceId, ordered);
}
